import { memo, useMemo, useRef } from 'react'
import { Badge, Group } from '@mantine/core'
import { IconUser } from '@tabler/icons-react'
import type { Agent } from '../../types/agent'
import classes from './AgentCard.module.css'
import type { AgentCardExtendedProps, AgentCardVariant } from './types'
import { trackComponentInteraction } from '../../../../utils/analytics'

const iconSize = 24

function defaultAria(agent: Agent) {
  const pinned = agent.pinned ? ' (pinned)' : ''
  return `Agent: ${agent.display_name || agent.name}${pinned}`
}

export const AgentCard = memo(function AgentCard({
  agent,
  onClick,
  onPin: _onPin,
  onUnpin: _onUnpin,
  disabled = false,
  variant = 'large',
  tabIndex = 0,
  ariaLabel,
}: AgentCardExtendedProps) {
  const Icon = IconUser
  const label = useMemo(() => ariaLabel || defaultAria(agent), [ariaLabel, agent])
  const isSmall = variant === 'small'
  const rootRef = useRef<HTMLButtonElement | null>(null)

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (disabled) {
      e.preventDefault()
      return
    }

    // Track agent interaction
    trackComponentInteraction(
      'AgentsModal',
      agent.display_name || agent.name,
      'agent',
      'click',
      'agent_interaction'
    )

    onClick?.(agent)
  }

  const handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (e) => {
    if (disabled) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()

      // Track agent interaction
      trackComponentInteraction(
        'AgentsModal',
        agent.display_name || agent.name,
        'agent',
        'click',
        'agent_interaction'
      )

      onClick?.(agent)
    }
  }

  const variantRowClass = isSmall ? classes.small : classes.large
  const variantContentClass = isSmall ? classes.contentSmall : classes.contentLarge
  const variantDescClamp = isSmall ? classes.descClampSmall : classes.descClampLarge
  const rootVariantClass = isSmall ? classes.rootSmall : classes.rootLarge

  return (
    <button
      ref={rootRef}
      type="button"
      aria-label={label}
      disabled={disabled}
      className={`${classes.root} ${rootVariantClass} ${disabled ? classes.disabled : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : tabIndex}
    >
      <Group gap={0} className={`${classes.row} ${variantRowClass}`} wrap="nowrap">
        <Icon size={iconSize} className={classes.icon} aria-hidden stroke={2} />
        <div className={`${classes.content} ${variantContentClass}`}>
          <Group gap={8} wrap="nowrap">
            <div className={classes.title}>{agent.display_name || agent.name}</div>
            {agent.tag && (
              <Badge size="xs" variant="light" color="blue" className={classes.betaBadge}>
                {agent.tag}
              </Badge>
            )}
          </Group>
          <div className={`${classes.desc} ${variantDescClamp}`}>{agent.description}</div>
        </div>
      </Group>
    </button>
  )
})

export type { AgentCardVariant }
